import { useState, type MouseEvent } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  useApproveUpsell,
  type UpsellWithLines,
  type UpsellLine,
} from "@/hooks/use-upsells";
import { useOfferLineTemplates } from "@/hooks/use-offers";
import { useSystemConfigNumber } from "@/hooks/use-system-config";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useProjectDetail } from "@/hooks/use-project-detail";
import { useRBAC } from "@/hooks/use-rbac";
import { OfferCatalogDialog } from "@/components/offer/OfferCatalogDialog";
import { useCopyUpsell } from "@/hooks/use-copy-upsell";
import { PHASE_ORDER } from "@/components/offer/offer-phase-tokens";
import type { OfferPhase } from "@/lib/offer-calc";
import {
  upsellLineToEditable,
  editableToUpsellPatch,
  resolveUpsellLineRate,
  buildTemplateHourlyRates,
  type EditableLine,
} from "@/lib/editable-line";
import type { OfferLine } from "@/hooks/use-offers";
import { computeUpsellTotals } from "@/lib/upsell-totals";
import { useUpsellEditState } from "@/hooks/use-upsell-edit-state";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import {
  isBillingModeLocked,
  computeBillingModeLockFromContext,
} from "@/lib/upsell-billing-mode-lock";
import { UpsellAmountFields } from "./upsell-edit/UpsellAmountFields";
import { UpsellPhaseSection } from "./upsell-edit/UpsellPhaseSection";
import { UpsellTotalsSummary } from "./upsell-edit/UpsellTotalsSummary";
import { UpsellApprovalBox } from "./upsell-edit/UpsellApprovalBox";
import { UpsellBillingModeFields } from "./upsell-edit/UpsellBillingModeFields";
import { UpsellEditHeader } from "./upsell-edit/UpsellEditHeader";
import { UpsellEditFooter } from "./upsell-edit/UpsellEditFooter";

interface Props {
  projectId: string;
  upsell: UpsellWithLines;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  readOnly?: boolean;
  /** Prosjektets default overhead-prosent — brukes når upsell.overhead_pct er null. */
  defaultOverheadPct?: number;
}

export function UpsellEditDialog({
  projectId,
  upsell,
  open,
  onOpenChange,
  readOnly = false,
  defaultOverheadPct = 0,
}: Props) {
  const [pickerPhase, setPickerPhase] = useState<OfferPhase | null>(null);
  const [approveOnSave, setApproveOnSave] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const {
    description,
    setDescription,
    overheadInput,
    setOverheadInput,
    discountPctInput,
    setDiscountPctInput,
    discountAmountInput,
    setDiscountAmountInput,
    billingMode,
    setBillingMode,
    lines,
    updateLineLocal,
    addLineFromTemplate,
    addFreeTextLine,
    deleteLineLocal,
    isDirty,
    save,
    discard,
    isSaving,
  } = useUpsellEditState(upsell, projectId, open);

  const tmUpsellEnabled = useFeatureFlag("feature_tm_upsell");
  const { data: templates = [] } = useOfferLineTemplates();
  const { data: hourlyRate = 2000 } = useSystemConfigNumber("hourly_rate");

  // VIB-2097: default_rate er per default_unit — normaliser dag-maler til kr/t.
  const rateByTemplate = buildTemplateHourlyRates(templates);
  const resolveRate = (line: UpsellLine): number =>
    resolveUpsellLineRate(line.template_id, rateByTemplate, hourlyRate);
  const { activeUser } = useImpersonation();
  const { isAdmin } = useRBAC();
  const { data: projectDetail } = useProjectDetail(projectId);
  const approveMut = useApproveUpsell();
  const { copyUpsell, copying } = useCopyUpsell();

  const isProductionLead =
    !!activeUser?.id && projectDetail?.project.production_lead_id === activeUser.id;
  const canApprove = !readOnly && (isAdmin || isProductionLead);

  const billingModeLocked = isBillingModeLocked(
    computeBillingModeLockFromContext({
      upsellId: upsell.id,
      addedToInvoiceId: upsell.added_to_invoice_id,
      budgetLines: projectDetail?.budgetLines ?? [],
      timeEntries: projectDetail?.timeEntries ?? [],
      invoices: projectDetail?.invoices ?? [],
    }),
  );

  const linesByPhase = new Map<OfferPhase, UpsellLine[]>();
  for (const p of PHASE_ORDER) linesByPhase.set(p, []);
  for (const l of lines) {
    const phase = (l.phase ?? "production") as OfferPhase;
    const bucket = linesByPhase.get(phase);
    if (bucket) bucket.push(l);
    else linesByPhase.get("production")!.push(l);
  }

  const draftOverheadPct = (() => {
    const raw = overheadInput.trim();
    if (raw === "") return null;
    const num = Number(raw);
    return Number.isFinite(num) ? num : upsell.overhead_pct;
  })();

  const totals = computeUpsellTotals({
    lines,
    overhead_pct: draftOverheadPct,
    discount_pct: Number(discountPctInput) || 0,
    discount_amount: Number(discountAmountInput) || 0,
    defaultOverheadPct,
  });

  const isDraft = upsell.status === "draft";

  function requestClose() {
    if (readOnly || !isDirty) {
      onOpenChange(false);
      return;
    }
    setConfirmCloseOpen(true);
  }

  function handleDialogOpenChange(next: boolean) {
    if (next) {
      onOpenChange(true);
      return;
    }
    requestClose();
  }

  function confirmDiscardAndClose() {
    discard();
    setConfirmCloseOpen(false);
    onOpenChange(false);
  }

  async function confirmSaveAndClose(e: MouseEvent) {
    e.preventDefault();
    const ok = await save();
    if (!ok) return;
    setConfirmCloseOpen(false);
    onOpenChange(false);
  }

  function handleApproveDirectly() {
    if (!activeUser?.id || isDirty) return;
    approveMut.mutate({
      id: upsell.id,
      project_id: projectId,
      approved_by: activeUser.id,
    });
  }

  function handleLineChange(l: UpsellLine, patch: Partial<OfferLine>) {
    const base = upsellLineToEditable(l, resolveRate(l));
    const merged: EditableLine = {
      ...base,
      description: patch.description ?? base.description,
      quantity: patch.quantity ?? base.quantity,
      unit: patch.unit ?? base.unit,
      unit_price: patch.unit_price ?? base.unit_price,
      purchase_price: patch.purchase_price ?? base.purchase_price,
      cost_type: patch.cost_type ?? base.cost_type,
      rate_basis_hourly:
        patch.rate_basis_hourly !== undefined ? patch.rate_basis_hourly : base.rate_basis_hourly,
    };
    const upsellPatch = editableToUpsellPatch(merged, resolveRate(l));
    updateLineLocal(l.id, {
      description: upsellPatch.description,
      phase: upsellPatch.phase,
      cost_type: upsellPatch.cost_type,
      unit: upsellPatch.unit,
      quantity: upsellPatch.quantity,
      quantity_hours: upsellPatch.quantity_hours,
      amount: upsellPatch.amount,
      estimated_cost: upsellPatch.estimated_cost,
      rate_basis_hourly: upsellPatch.rate_basis_hourly,
    });
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <UpsellEditHeader
            isDraft={isDraft}
            attachmentPath={upsell.attachment_path}
            attachmentFilename={upsell.attachment_filename}
          />

          <div className="space-y-4">
            {tmUpsellEnabled && (
              <UpsellBillingModeFields
                billingMode={billingMode}
                onChange={setBillingMode}
                readOnly={readOnly}
                locked={billingModeLocked}
              />
            )}

            <UpsellAmountFields
              description={description}
              onDescriptionChange={setDescription}
              overheadInput={overheadInput}
              onOverheadChange={setOverheadInput}
              discountPctInput={discountPctInput}
              onDiscountPctChange={setDiscountPctInput}
              discountAmountInput={discountAmountInput}
              onDiscountAmountChange={setDiscountAmountInput}
              defaultOverheadPct={defaultOverheadPct}
              readOnly={readOnly}
              billingMode={billingMode}
            />

            {PHASE_ORDER.map((phase) => (
              <UpsellPhaseSection
                key={phase}
                phase={phase}
                lines={linesByPhase.get(phase) ?? []}
                resolveRate={resolveRate}
                readOnly={readOnly}
                onAddFromCatalog={() => setPickerPhase(phase)}
                onAddFreeText={() => addFreeTextLine(phase)}
                onLineChange={handleLineChange}
                onLineDelete={(l) => deleteLineLocal(l.id)}
              />
            ))}

            {billingMode !== "tm" && lines.length > 0 && (
              <UpsellTotalsSummary
                lineSum={totals.lineSum}
                overheadAmount={totals.overheadAmount}
                effectiveOverheadPct={totals.effectiveOverheadPct}
                effectiveDiscount={totals.effectiveDiscount}
                total={totals.total}
                totalMargin={totals.totalMargin}
              />
            )}

            {isDraft && canApprove && (
              <UpsellApprovalBox
                approveOnSave={approveOnSave}
                onApproveOnSaveChange={setApproveOnSave}
                onApproveDirectly={handleApproveDirectly}
                isApproving={approveMut.isPending}
                approveDisabled={isDirty}
                approveDisabledReason="Lagre endringene før du godkjenner."
              />
            )}
          </div>

          <UpsellEditFooter
            onCopy={() =>
              copyUpsell(
                {
                  description,
                  overhead_pct: draftOverheadPct,
                  discount_pct: Number(discountPctInput) || 0,
                  discount_amount: Number(discountAmountInput) || 0,
                  lines,
                },
                defaultOverheadPct,
              )
            }
            copying={copying}
            hasLines={lines.length > 0}
            onClose={requestClose}
            onSave={() => void save()}
            isDirty={isDirty}
            isSaving={isSaving}
            readOnly={readOnly}
          />

          {pickerPhase && (
            <OfferCatalogDialog
              open={pickerPhase !== null}
              onOpenChange={(o) => {
                if (!o) setPickerPhase(null);
              }}
              templates={templates}
              restrictToPhase={pickerPhase}
              onPick={(t) => {
                addLineFromTemplate(pickerPhase, t);
                setPickerPhase(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ulagrede endringer</AlertDialogTitle>
            <AlertDialogDescription>
              Du har endringer som ikke er lagret. Lagre før du lukker, eller lukk uten å lagre.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              className={cn(buttonVariants({ variant: "destructive" }))}
              onClick={confirmDiscardAndClose}
              disabled={isSaving}
            >
              Lukk uten å lagre
            </AlertDialogAction>
            <AlertDialogAction onClick={(e) => void confirmSaveAndClose(e)} disabled={isSaving}>
              Lagre
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
